import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);

const Section = ({ title, onAdd, testid, children }) => (
  <div>
    <div className="flex justify-between items-center mb-2">
      <h4 className="font-bold text-sm">{title}</h4>
      <Button type="button" size="sm" variant="outline" className="h-7" onClick={onAdd} data-testid={testid}><Plus className="w-3 h-3 mr-1" />Add</Button>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Del = ({ onClick, testid }) => <button type="button" onClick={onClick} className="p-1.5 text-slate-500 hover:text-red-600" data-testid={testid}><Trash2 className="w-4 h-4" /></button>;

export const ProgressDialog = ({ enrollment, auth, onDone, endpoint = "admin" }) => {
  const init = enrollment.progress || {};
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(!!init.program_completed);
  const [att, setAtt] = useState(init.attendance || []);
  const [proj, setProj] = useState(init.projects || []);
  const [earn, setEarn] = useState(init.earnings || []);
  const [note, setNote] = useState(init.teacher_note || "");
  const [loading, setLoading] = useState(false);

  const upd = (setter) => (i, k, v) => setter((cur) => cur.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const del = (setter) => (i) => setter((cur) => cur.filter((_, j) => j !== i));

  const save = async () => {
    setLoading(true);
    try {
      await api.put(`/${endpoint}/enrollments/${enrollment.id}/progress`, {
        attendance: att, projects: proj.filter((p) => p.title.trim()),
        earnings: earn.map((e) => ({ ...e, amount_inr: Number(e.amount_inr) || 0 })), teacher_note: note,
        program_completed: done, expected_updated_at: enrollment.progress?.updated_at || "",
      }, auth);
      toast.success("Progress saved");
      setOpen(false);
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409) { toast.error(d); setOpen(false); onDone(); return; }
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(", ") : d || "Save failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-10 sm:h-8" data-testid={`progress-btn-${enrollment.id}`}><ClipboardList className="w-3.5 h-3.5 mr-1" />Progress</Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined} data-testid="progress-dialog">
        <DialogHeader><DialogTitle>{enrollment.student_name} — {enrollment.program} (#{enrollment.id.slice(0, 8).toUpperCase()})</DialogTitle></DialogHeader>
        <div className="space-y-6">
          <Section title={`Attendance (${att.filter((a) => a.present).length}/${att.length})`} onAdd={() => setAtt([...att, { date: today(), present: true }])} testid="add-attendance-btn">
            {att.map((a, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`attendance-row-${i}`}>
                <Input type="date" value={a.date} onChange={(e) => upd(setAtt)(i, "date", e.target.value)} className="h-9" />
                <button type="button" onClick={() => upd(setAtt)(i, "present", !a.present)} data-testid={`attendance-toggle-${i}`}
                  className={`px-3 h-9 rounded-md text-xs font-bold w-24 ${a.present ? "bg-emerald-500/15 text-emerald-700" : "bg-red-500/15 text-red-600"}`}>{a.present ? "Present" : "Absent"}</button>
                <Del onClick={() => del(setAtt)(i)} testid={`attendance-del-${i}`} />
              </div>
            ))}
          </Section>
          <Section title="Projects" onAdd={() => setProj([...proj, { title: "", status: "in_progress", date: today() }])} testid="add-project-btn">
            {proj.map((p, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`project-row-${i}`}>
                <Input placeholder="Project title" value={p.title} onChange={(e) => upd(setProj)(i, "title", e.target.value)} className="h-9" data-testid={`project-title-${i}`} />
                <button type="button" onClick={() => upd(setProj)(i, "status", p.status === "completed" ? "in_progress" : "completed")} data-testid={`project-status-${i}`}
                  className={`px-3 h-9 rounded-md text-xs font-bold w-28 shrink-0 ${p.status === "completed" ? "bg-emerald-500/15 text-emerald-700" : "bg-white text-slate-600"}`}>{p.status === "completed" ? "Completed" : "In progress"}</button>
                <Del onClick={() => del(setProj)(i)} testid={`project-del-${i}`} />
              </div>
            ))}
          </Section>
          <Section title="Learn & Earn earnings (₹)" onAdd={() => setEarn([...earn, { amount_inr: "", note: "", date: today() }])} testid="add-earning-btn">
            {earn.map((x, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`earning-row-${i}`}>
                <Input type="number" min="0" placeholder="Amount" value={x.amount_inr} onChange={(e) => upd(setEarn)(i, "amount_inr", e.target.value)} className="h-9 w-32" data-testid={`earning-amount-${i}`} />
                <Input placeholder="For (e.g. IoT kit sale)" value={x.note} onChange={(e) => upd(setEarn)(i, "note", e.target.value)} className="h-9" data-testid={`earning-note-${i}`} />
                <Del onClick={() => del(setEarn)(i)} testid={`earning-del-${i}`} />
              </div>
            ))}
          </Section>
          <div>
            <h4 className="font-bold text-sm mb-2">Mentor's note for parents</h4>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="teacher-note-input" />
          </div>
          <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-[#FF2E63]/40 bg-[#FF2E63]/10 cursor-pointer" data-testid="program-completed-label">
            <input type="checkbox" checked={done} onChange={(e) => setDone(e.target.checked)} className="w-4 h-4 accent-[#FF2E63]" data-testid="program-completed-checkbox" />
            <span className="text-sm"><b>Program completed</b> — parents can then download the RoboSpark certificate{init.certificate_no ? ` (${init.certificate_no})` : ""}</span>
          </label>
          <Button onClick={save} disabled={loading} className="w-full rs-cta rounded-full" data-testid="progress-save-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save progress"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
